import * as React from 'react';
import { isValidElement, cloneElement, createElement, Component } from 'react';
import { findDOMNode } from 'react-dom';
import ResizeObserver from 'resize-observer-polyfill';
import rafSchd from 'raf-schd';
import ResizeDetectorChildWrapper from '../Components/ResizeDetectorChildWrapper';
import { debounce, throttle } from 'lodash';
import { isDOMElement } from '../utils/isDOMElement';
import { isFunction } from '../utils/isFunction';
import ResizeDetectorProps from '../models/components/ResizeDetectorProps';

export const listHandle = {
    debounce,
    throttle,
};

class ResizeDetector extends Component<ResizeDetectorProps, any> {
    static defaultProps = {
        handleWidth: false,
        handleHeight: false,
        skipOnMount: false,
        querySelector: null,
        targetDomElement: null,
        onResize: null,
        render: undefined,
        children: null,
        nodeType: 'div',
    };

    skipOnMount: boolean;
    raf: any;
    element: any;
    unmounted: boolean;
    resizeHandler: any;
    resizeObserver: ResizeObserver;

    constructor(props) {
        super(props);
        const { skipOnMount } = props;

        this.state = {
            width: undefined,
            height: undefined,
        };

        this.skipOnMount = skipOnMount;
        this.element = null;
        this.unmounted = false;
        this.resizeHandler = this.createResizeHandler;
        this.resizeObserver = new ResizeObserver(this.resizeHandler);
    }

    componentDidMount() {
        this.toggleObserver('observe');
    }

    componentWillUnmount() {
        this.toggleObserver('unobserve');
        this.rafClean();
        this.cancelHandler();
        this.unmounted = true;
    }

    cancelHandler = () => {
        if (this.resizeHandler && this.resizeHandler.cancel) {
            // cancel debounced handler
            this.resizeHandler.cancel();
            this.resizeHandler = null;
        }
    };

    rafClean = () => {
        if (this.raf && this.raf.cancel) {
            this.raf.cancel();
            this.raf = null;
        }
    };

    toggleObserver = type => {
        const element = this.getElement();
        if (!element || !this.resizeObserver[type]) {
            return;
        }
        this.resizeObserver[type](element);
    };

    getElement = () => {
        const { querySelector, targetDomElement } = this.props;

        if (querySelector) {
            return document.querySelector(querySelector);
        }

        if (targetDomElement && isDOMElement(targetDomElement)) {
            return targetDomElement;
        }

        const currentElement = this.element && findDOMNode(this.element);
        return currentElement && currentElement.parentElement;
    };

    createUpdater = () => {
        this.rafClean();

        this.raf = rafSchd(({ width, height }) => {
            const { onResize } = this.props;

            if (isFunction(onResize)) {
                onResize(width, height);
            }

            this.setState({ width, height });
        });

        return this.raf;
    };

    createResizeHandler = entries => {
        const { width: widthCurrent, height: heightCurrent } = this.state;
        const { handleWidth, handleHeight } = this.props;

        if (!handleWidth && !handleHeight) {
            return;
        }

        const updater = this.createUpdater();

        entries.forEach(entry => {
            const { width, height } = (entry && entry.contentRect) || {};

            const isWidthChanged = handleWidth && widthCurrent !== width;
            const isHeightChanged = handleHeight && heightCurrent !== height;
            const isSizeChanged = isWidthChanged || isHeightChanged;

            const shouldSetSize = !this.skipOnMount && isSizeChanged;
            if (shouldSetSize) {
                updater({ width, height });
            }

            this.skipOnMount = false;
        });
    };

    onRef = el => {
        this.element = el;
    };

    getRenderType = () => {
        const { render, children } = this.props;
        if (isFunction(render)) {
            return 'renderProp';
        }

        if (isFunction(children)) {
            return 'childFunction';
        }

        if (isValidElement(children)) {
            return 'child';
        }

        if (Array.isArray(children)) {
            return 'childArray';
        }

        return 'parent';
    };

    getTargetComponent = () => {
        const { render, children, nodeType } = this.props;
        const { width, height } = this.state;

        const childProps = { width, height };
        const renderType = this.getRenderType();

        switch (renderType) {
            case 'renderProp':
                return cloneElement(render(childProps), {
                    key: 'resize-detector',
                });
            case 'childFunction':
                return cloneElement(children(childProps));
            case 'child':
                return cloneElement(children, childProps);
            case 'childArray':
                return children.map(el => !!el && cloneElement(el, childProps));
            default:
                return createElement(nodeType);
        }
    };

    render() {
        return (
            <ResizeDetectorChildWrapper ref={this.onRef}>
                {this.getTargetComponent()}
            </ResizeDetectorChildWrapper>
        );
    }
}

export default ResizeDetector;



// WEBPACK FOOTER //
// ./src/Components/ResizeDetector.tsx